export type GoogleAddressComponent = {
  longText?: string;
  shortText?: string;
  types: string[];
};

export type GoogleAutocompleteSuggestion = {
  placePrediction?: GooglePlacePrediction;
};

export type GoogleLatLng = {
  lat: () => number;
  lng: () => number;
};

export type GooglePlace = {
  fetchFields: (options: { fields: string[] }) => Promise<{
    place: GooglePlaceDetails;
  }>;
};

export type GooglePlaceDetails = {
  addressComponents?: GoogleAddressComponent[];
  formattedAddress?: string;
  location?: GoogleLatLng | null;
};

export type GooglePlacePrediction = {
  mainText?: { toString(): string };
  placeId: string;
  secondaryText?: { toString(): string };
  text: { toString(): string };
  toPlace(): GooglePlace;
  types?: string[];
};

export type GooglePlacesLibrary = {
  AutocompleteSessionToken: new () => unknown;
  AutocompleteSuggestion: {
    fetchAutocompleteSuggestions: (request: {
      includedPrimaryTypes?: string[];
      includedRegionCodes?: string[];
      input: string;
      sessionToken?: unknown;
    }) => Promise<{ suggestions: GoogleAutocompleteSuggestion[] }>;
  };
};

declare global {
  interface Window {
    google?: {
      maps?: {
        __ib__?: () => void;
        importLibrary?: (library: string) => Promise<unknown>;
        places?: Partial<GooglePlacesLibrary>;
      };
    };
  }
}

const GOOGLE_MAPS_API_KEY_ENV = "VITE_GOOGLE_MAPS_API_KEY";
const GOOGLE_MAPS_BOOTSTRAP_TIMEOUT_MS = 10_000;

let googleMapsAPIKeyLoaded: string | null = null;
let googleMapsLoadPromise: Promise<void> | null = null;

export function readGoogleMapsAPIKey() {
  const value = import.meta.env[GOOGLE_MAPS_API_KEY_ENV] as string | undefined;
  const trimmed = value?.trim() ?? "";
  return trimmed || null;
}

export async function loadGooglePlacesLibrary(
  apiKey: string,
): Promise<GooglePlacesLibrary> {
  const existingPlaces = window.google?.maps?.places;
  if (
    existingPlaces?.AutocompleteSessionToken &&
    existingPlaces?.AutocompleteSuggestion
  ) {
    return existingPlaces as GooglePlacesLibrary;
  }

  const importLibrary = window.google?.maps?.importLibrary;
  if (importLibrary) {
    return (await importLibrary("places")) as GooglePlacesLibrary;
  }

  await loadGoogleMapsScript(apiKey);

  const nextImportLibrary = window.google?.maps?.importLibrary;
  if (nextImportLibrary) {
    return (await nextImportLibrary("places")) as GooglePlacesLibrary;
  }

  const nextPlaces = window.google?.maps?.places;
  if (
    nextPlaces?.AutocompleteSessionToken &&
    nextPlaces?.AutocompleteSuggestion
  ) {
    return nextPlaces as GooglePlacesLibrary;
  }

  throw new Error("Google Maps Places library did not load.");
}

function loadGoogleMapsScript(apiKey: string) {
  if (typeof window.google?.maps?.importLibrary === "function") {
    return Promise.resolve();
  }

  if (googleMapsLoadPromise && googleMapsAPIKeyLoaded === apiKey) {
    return googleMapsLoadPromise;
  }

  googleMapsAPIKeyLoaded = apiKey;
  googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
    const googleNamespace = (window.google ??= {});
    const mapsNamespace = (googleNamespace.maps ??= {});
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-sill-google-maps="true"]',
    );
    if (existing) {
      existing.remove();
    }

    let settled = false;
    const bootstrapTimeout = window.setTimeout(() => {
      settleReject("Google Maps Places bootstrap timed out.");
    }, GOOGLE_MAPS_BOOTSTRAP_TIMEOUT_MS);
    const settleResolve = () => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(bootstrapTimeout);
      resolve();
    };
    const settleReject = (message: string) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(bootstrapTimeout);
      reject(new Error(message));
    };

    mapsNamespace.__ib__ = () => {
      if (typeof window.google?.maps?.importLibrary !== "function") {
        settleReject("Google Maps loaded without importLibrary.");
        return;
      }
      settleResolve();
    };

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.sillGoogleMaps = "true";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&callback=google.maps.__ib__&loading=async&libraries=places&v=weekly`;
    script.addEventListener(
      "load",
      () => {
        if (typeof window.google?.maps?.importLibrary === "function") {
          settleResolve();
        }
      },
      { once: true },
    );
    script.addEventListener(
      "error",
      () => settleReject("Google Maps failed to load."),
      { once: true },
    );
    document.head.append(script);
  })
    .finally(() => {
      if (window.google?.maps) {
        delete window.google.maps.__ib__;
      }
    })
    .catch((error) => {
      googleMapsLoadPromise = null;
      throw error;
    });

  return googleMapsLoadPromise;
}
